import { useEffect, useRef, useState } from "react";
import {
  X,
  Upload,
  Download,
  Info,
  Database,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  RotateCcw,
  Trash2,
  Zap,
} from "lucide-react";
import {
  DATASETS,
  buildTemplateCsv,
  readFileToRows,
  validateAndCoerce,
  type DatasetDef,
} from "@/data/datasets";
import {
  applyImport,
  clearDataset,
  resetToDemo,
  getInfo,
  isImported,
  useDataVersion,
  type DatasetKey,
} from "@/data/store";
import { downloadText } from "@/lib/download";
import { cn } from "@/lib/utils";

interface Feedback {
  status: "ok" | "error";
  messages: string[];
  count?: number;
  fileName?: string;
}

interface ImportPanelProps {
  open: boolean;
  onClose: () => void;
}

export function ImportPanel({ open, onClose }: ImportPanelProps) {
  useDataVersion(); // re-render la orice schimbare a sursei
  const info = getInfo();
  const [feedback, setFeedback] = useState<Record<string, Feedback>>({});
  const [busyKey, setBusyKey] = useState<DatasetKey | null>(null);

  // Blochează scroll-ul din spate cât panoul e deschis.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Închide cu Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function handleFile(def: DatasetDef, file: File) {
    setBusyKey(def.key);
    try {
      const raw = await readFileToRows(file);
      const res = validateAndCoerce(def, raw);
      if (res.ok) {
        applyImport(def.key, res.rows!);
        setFeedback((f) => ({
          ...f,
          [def.key]: { status: "ok", messages: [], count: res.count, fileName: file.name },
        }));
      } else {
        setFeedback((f) => ({
          ...f,
          [def.key]: { status: "error", messages: res.errors, fileName: file.name },
        }));
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setFeedback((f) => ({
        ...f,
        [def.key]: {
          status: "error",
          messages: [`Nu am putut citi fișierul „${file.name}": ${msg}`],
          fileName: file.name,
        },
      }));
    } finally {
      setBusyKey(null);
    }
  }

  function handleReset() {
    resetToDemo();
    setFeedback({});
  }

  const generat = info.importedAt ? new Date(info.importedAt) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-3 backdrop-blur-sm sm:p-6"
      onClick={onClose}
    >
      <div
        className="my-4 w-full max-w-3xl rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-2xl border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
              <Upload className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900 sm:text-lg">Importă date reale</h2>
              <p className="truncate text-xs text-slate-500">
                Încarcă exporturile tale Excel (.xlsx) sau CSV — dashboard-ul se redesenează pe ele.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Închide"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          {/* De unde vin datele? */}
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm leading-relaxed text-blue-900">
            <div className="mb-1.5 flex items-center gap-2 font-semibold">
              <Info className="h-4 w-4 shrink-0" />
              De unde vin datele?
            </div>
            <ul className="ml-1 space-y-1.5">
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                <span>
                  <strong>Acum:</strong> descarci template-ul CSV al fiecărui set de mai jos, îl completezi
                  din exporturile tale WinMentor / Optima (Excel), și îl încarci înapoi. Totul rămâne în
                  browserul tău — nu se trimite nicăieri.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                <span>
                  <strong>Faza 2:</strong> conectorul automat <em>live</em> la baza de date WinMentor va aduce
                  datele singur, fără upload — se activează când avem acces la DB. Până atunci, importul manual
                  de aici e sursa reală.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
                <span>
                  Coloanele marcate <em>„auto"</em> le calculăm noi (marjă, medii mobile, procente) — tu
                  completezi doar cifrele brute.
                </span>
              </li>
            </ul>
          </div>

          {/* Stare curentă + reset */}
          <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Database className="h-4 w-4 shrink-0 text-slate-500" />
              {info.mode === "imported" ? (
                <span className="text-slate-700">
                  Sursă activă:{" "}
                  <span className="font-semibold text-emerald-700">date importate</span>
                  {generat && (
                    <span className="text-slate-500"> · {generat.toLocaleString("ro-RO")}</span>
                  )}
                  <span className="text-slate-500">
                    {" "}
                    · {info.importedKeys.length}/{DATASETS.length} seturi
                  </span>
                </span>
              ) : (
                <span className="text-slate-700">
                  Sursă activă: <span className="font-semibold text-amber-700">date demo (mock)</span>
                </span>
              )}
            </div>
            <button
              onClick={handleReset}
              disabled={info.mode !== "imported"}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                info.mode === "imported"
                  ? "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                  : "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400",
              )}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Revino la date demo
            </button>
          </div>

          {/* Carduri per set de date */}
          <div className="space-y-3">
            {DATASETS.map((def) => (
              <DatasetCard
                key={def.key}
                def={def}
                busy={busyKey === def.key}
                imported={isImported(def.key)}
                feedback={feedback[def.key]}
                onFile={(file) => handleFile(def, file)}
                onClear={() => {
                  clearDataset(def.key);
                  setFeedback((f) => {
                    const c = { ...f };
                    delete c[def.key];
                    return c;
                  });
                }}
              />
            ))}
          </div>

          <p className="text-center text-xs text-slate-400">
            Formate acceptate: .xlsx, .xls, .csv · parsare 100% în browser (SheetJS) · fără backend
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Card per set de date ─────────────────────────────────────────────────────
function DatasetCard({
  def,
  busy,
  imported,
  feedback,
  onFile,
  onClear,
}: {
  def: DatasetDef;
  busy: boolean;
  imported: boolean;
  feedback?: Feedback;
  onFile: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const obligatorii = def.columns.filter((c) => c.required && !c.auto);
  const automate = def.columns.filter((c) => c.auto);

  function downloadTemplate() {
    downloadText(def.fileName, buildTemplateCsv(def));
  }

  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-colors",
        imported ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white",
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 shrink-0 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-800">{def.titlu}</h3>
            {imported && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> importat
              </span>
            )}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">{def.descriere}</p>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
            <span className="font-medium text-slate-500">Sursă sugerată:</span> {def.sursaSugerata}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" />
            Template CSV
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-white",
              busy ? "cursor-wait bg-brand-400" : "bg-brand-600 hover:bg-brand-700",
            )}
          >
            <Upload className="h-3.5 w-3.5" />
            {busy ? "Se procesează…" : "Încarcă fișier"}
          </button>
        </div>
      </div>

      {/* Coloane */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {obligatorii.map((c) => (
          <span
            key={c.name}
            title={c.descriere}
            className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
          >
            {c.name}
          </span>
        ))}
        {automate.length > 0 && (
          <span className="text-[11px] text-slate-400">
            + {automate.length} auto ({automate.map((c) => c.name).join(", ")})
          </span>
        )}
      </div>

      {/* Feedback */}
      {feedback?.status === "ok" && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Import reușit din <strong>{feedback.fileName}</strong> — <strong>{feedback.count}</strong>{" "}
            rânduri încărcate. Dashboard-ul s-a actualizat.
          </span>
        </div>
      )}
      {feedback?.status === "error" && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Fișierul nu a putut fi importat
          </div>
          <ul className="mt-1.5 ml-1 space-y-1">
            {feedback.messages.map((m, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-rose-400" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {imported && (
        <button
          onClick={onClear}
          className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-rose-600"
        >
          <Trash2 className="h-3 w-3" />
          Scoate acest set (revino la demo pentru el)
        </button>
      )}

      {def.key === "media_mobila" && (
        <div className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-400">
          <Zap className="mt-0.5 h-3 w-3 shrink-0" />
          Se calculează automat din setul „Cost producție". Îl încarci separat doar dacă vrei valori proprii.
        </div>
      )}
    </div>
  );
}
